import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  UpdateUserProfileForm,
  UpdateUserProfileFormRef,
} from '@/components/user/UpdateUserProfileForm';
import { useUserProfile } from '@/hooks/user/useUserProfile';

export interface DetailsStepProps {
  /** Called after a successful save — used to snap a manually-reopened
   * review back to whatever step the user has actually progressed to. */
  onSaved?: () => void;
  /** Mobile only — portal the Continue button into this shared slot above
   * the footer instead of rendering it inline. */
  ctaSlot?: HTMLDivElement | null;
  isMobile?: boolean;
}

export const DetailsStep = ({ onSaved, ctaSlot, isMobile }: DetailsStepProps) => {
  const {
    userProfile,
    updateUserProfile,
    isUpdatingUserProfile,
    isFetchingUserProfile,
    isUserProfileUpdated,
    updateUserProfileError,
  } = useUserProfile();
  const formRef = useRef<UpdateUserProfileFormRef>(null);

  useEffect(() => {
    if (!isUpdatingUserProfile && isUserProfileUpdated) onSaved?.();
  }, [isUpdatingUserProfile, isUserProfileUpdated]);

  const continueButton = (
    <Button
      fullWidth
      onClick={() => formRef.current?.submitForm()}
      disabled={isUpdatingUserProfile || isFetchingUserProfile}
      data-testid="onboarding-details-submit-btn"
      sx={{
        height: 40,
        borderRadius: '10px',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        '&:hover': { backgroundColor: '#D6D3CC' },
      }}>
      {isUpdatingUserProfile ? <CircularProgress size={18} sx={{ color: '#262626' }} /> : 'Continue'}
    </Button>
  );

  return (
    <Stack sx={{ height: '100%', justifyContent: 'space-between' }}>
      <Stack spacing="20px">
        <Stack spacing="6px">
          <Typography sx={{ fontSize: 24, fontWeight: 600, color: 'var(--tx-text)' }}>
            Tell us about you
          </Typography>
          {!isMobile && (
            <Typography sx={{ fontSize: 13, color: '#918D87' }}>
              So we can personalize your Home HQ and keep you posted.
            </Typography>
          )}
        </Stack>

        {/* UpdateUserProfileForm is shared with staff's profile page, so its
            field labels and input background aren't touched at the source —
            scoped down here instead. Fields default to a solid but dark
            neutral fill that reads as translucent against the glass card;
            bumping it lighter makes them read as solid, opaque controls. */}
        <Box
          sx={{
            '& .MuiTypography-body2': { fontSize: '12px' },
            '& .MuiFilledInput-root': {
              backgroundColor: 'var(--tx-fill-2)',
              height: '40px',
            },
            // height:'100%' (not just zeroed padding) so the <input> itself
            // fills the full 40px pill — autofill highlighting (Chrome's own
            // and our theme's masking box-shadow in MuiInputBase, both scoped
            // to the input element's own box) only covers whatever box the
            // input actually renders at, so padding alone left it a
            // vertically-centered sliver with the browser's blue showing
            // through above and below.
            '& .MuiFilledInput-input': {
              paddingTop: 0,
              paddingBottom: 0,
              height: '100%',
              boxSizing: 'border-box',
            },
            '& .MuiAutocomplete-option': { fontSize: '13px' },
            ...(isMobile && { '& .MuiTypography-body3': { fontSize: '13px' } }),
          }}>
          <UpdateUserProfileForm
            ref={formRef}
            values={userProfile}
            onSubmit={(values) => updateUserProfile(values, { markUserProfileAsComplete: true })}
            hideEmail={isMobile}
            collapsibleMarketingConsent={isMobile}
            rowSpacing={isMobile ? 2.5 : undefined}
          />
        </Box>

        {updateUserProfileError && (
          <Typography sx={{ fontSize: 12, color: '#d9799b' }}>{updateUserProfileError}</Typography>
        )}
      </Stack>

      {!ctaSlot && continueButton}
      {ctaSlot && createPortal(continueButton, ctaSlot)}
    </Stack>
  );
};
